// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCardComparison } from 'src/contexts/CardComparisonContext'
import { ArrowBack, Settings } from '@mui/icons-material'
import { EqCardKey, EqCardType, type GetContractResponseContent } from 'src/types/contract.types'
import {
  AlbumCardView,
  CostCardView,
  OwnershipDetailsCard,
  PercentageView,
  TermLengthDetailsCard,
  TermView,
  TimeTermView
} from '@/components/contract-viewer/eq-cards'
import useSWR from 'swr'
import { getContractApi } from 'src/api'
import { useDemoContractAnalysis } from '@/hooks/contracts/useDemoContractAnalysis'
import { ContractSelectionPopup } from './ContractSelectionPopup'
import { Button } from '@/components/ui/Button'
import { toProperCase } from 'src/utils/Strings'

interface CardComparisonPageProps {
  isDemo?: boolean
}

export const CardComparisonPage: React.FC<CardComparisonPageProps> = ({ isDemo = false }) => {
  const { selectedCards, selectedContracts, currentContractId, isLoading: isContextLoading, setSelectedContracts } = useCardComparison()
  const { musicIndustryContracts } = useDemoContractAnalysis()
  const navigate = useNavigate()
  const [isPopupOpen, setIsPopupOpen] = useState(false)

  // Redirect back if no cards selected (but wait for loading to complete)
  useEffect(() => {
    if (!isContextLoading && selectedCards.length === 0) {
      navigate(isDemo ? '/demo/analysis' : '/analysis')
    }
  }, [selectedCards, navigate, isDemo, isContextLoading])

  // Include current contract along with selected contracts
  const allContractIds = useMemo(() => {
    const ids = [...selectedContracts]
    // Add current contract at the beginning if it's not already in the list
    if (currentContractId && !ids.includes(currentContractId)) {
      ids.unshift(currentContractId)
    }
    return ids
  }, [selectedContracts, currentContractId])

  // Fetch all selected contracts at once
  const contractsFetcher = async (contractIds: string[]): Promise<GetContractResponseContent[]> => {
    const promises = contractIds.map(async (contractId) => {
      if (isDemo) {
        const demoContract = musicIndustryContracts.find(c => c.contractId === contractId)
        if (!demoContract) throw new Error('Contract not found')
        return demoContract
      }
      return getContractApi(contractId)
    })
    return Promise.all(promises)
  }

  // Single SWR hook for all contracts
  const { data: contractsData, isLoading: isLoadingContracts, error } = useSWR(
    allContractIds.length > 0 ? ['contracts-comparison', allContractIds] : null,
    () => contractsFetcher(allContractIds)
  )

  const isLoadingAny = isLoadingContracts
  const hasError = !!error

  // Get unique card keys from selected cards
  const selectedCardKeys = useMemo(() => {
    return Array.from(new Set(selectedCards.map(card => card.cardKey)))
  }, [selectedCards])

  // Group cards by card key, with all contracts' versions
  const comparisonGroups = useMemo(() => {
    if (isLoadingAny || !contractsData) {
      return []
    }

    return selectedCardKeys.map(cardKey => {
      const originalCard = selectedCards.find(c => c.cardKey === cardKey)
      if (!originalCard) return null

      // Get cards from all contracts (including current contract)
      const cards = allContractIds.map((contractId, index) => {
        const contractData = contractsData[index]
        if (!contractData) return null

        const cardData = contractData.eqData?.cards?.[cardKey as EqCardKey]
        return {
          contractId,
          contractName: contractData.name,
          contractType: contractData.type,
          cardData,
          cardKey,
          isCurrentContract: contractId === currentContractId
        }
      }).filter((card): card is NonNullable<typeof card> => card !== null)

      return {
        cardKey,
        title: originalCard.sectionTitle,
        cards
      }
    }).filter((group): group is NonNullable<typeof group> => group !== null)
  }, [selectedCardKeys, selectedCards, allContractIds, contractsData, isLoadingAny, currentContractId])

  const handleClose = () => {
    navigate(-1)
  }

  const handleContractSelection = (contractIds: string[]) => {
    setSelectedContracts(contractIds)
    setIsPopupOpen(false)
  }

  // Get the appropriate card component based on card key and type
  const getCardComponent = (cardKey: EqCardKey, cardType?: EqCardType) => {
    switch (cardKey) {
      case EqCardKey.moneyYouReceive:
        return TermView
      case EqCardKey.whatYouOwn:
        return cardType === EqCardType.B ? OwnershipDetailsCard : PercentageView
      case EqCardKey.whatYoureResponsibleFor:
        return AlbumCardView
      case EqCardKey.howLongThisDealLasts:
        return cardType === EqCardType.B ? TermLengthDetailsCard : TimeTermView
      case EqCardKey.risksCostsLegalStuff:
        return CostCardView
      default:
        return TermView
    }
  }

  return (
    <div className="min-h-screen text-white p-8">
      <div className="w-full max-w-7xl mx-auto">
        {/* Context Loading State */}
        {isContextLoading && (
          <div className="text-center py-16">
            <div className="text-white/70 text-lg">Loading comparison data...</div>
          </div>
        )}

        {/* Main Content - Only show after context is loaded */}
        {!isContextLoading && (
          <>
            {/* Header */}
            <div className="mb-8">
              <Button
                className="mb-16"
                variant="secondary"
                onClick={handleClose}
              >
                <ArrowBack className="w-5 h-5" />
                Back to Analysis
              </Button>
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-4xl font-bold mb-2 text-white">
                    Contract Comparison
                  </h1>
                  <p className="text-white/70 text-lg">
                    Comparing {selectedCardKeys.length} card type{selectedCardKeys.length !== 1 ? 's' : ''}
                    {allContractIds.length > 0 && ` across ${allContractIds.length} contract${allContractIds.length !== 1 ? 's' : ''}`}
                  </p>
                </div>
                <button
                  onClick={() => setIsPopupOpen(true)}
                  className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 flex items-center gap-2 transition-colors"
                >
                  <Settings className="w-5 h-5" />
                  Adjust Contracts
                </button>
              </div>
            </div>

            {/* Contract Loading State */}
            {isLoadingAny && (
              <div className="text-center py-16">
                <div className="text-white/70 text-lg">Loading contracts...</div>
              </div>
            )}

        {/* Error State */}
        {hasError && !isLoadingAny && (
          <div className="text-center py-16">
            <div className="text-red-400 text-lg mb-4">Error loading contracts</div>
            <Button
              onClick={handleClose}
              className="px-6 py-3 bg-gradient-to-r from-[#547bb5] to-[#6e53b8] rounded-full text-white font-medium hover:opacity-90 transition-opacity"
            >
              Go Back
            </Button>
          </div>
        )}

        {/* Content - Grouped by card type */}
        {!isLoadingAny && !hasError && (
          <div className="space-y-12">
            {comparisonGroups.map((group) => (
              <div key={group.cardKey} className="space-y-6">
                {/* Section Header */}
                <div className="flex items-center gap-4">
                  <div className="text-white/70 bg-white/5 rounded-full px-4 py-2 text-sm font-medium">
                    {group.title}
                  </div>
                  <div className="flex-1 h-px bg-gradient-to-r from-white/20 to-transparent" />
                </div>

                {/* Cards Grid - Side by Side */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {group.cards.map((card: any) => {
                    const Component = getCardComponent(group.cardKey as EqCardKey, card.cardData?.type)
                    const handleViewContract = () => {
                      const analysisPath = isDemo ? `/demo/analysis/${card.contractId}` : `/analysis/${card.contractId}`
                      navigate(analysisPath)
                    }
                    return (
                      <div key={card.contractId} className="space-y-3">
                        {/* Contract Label */}
                        <div className="flex flex-col gap-2 px-3">
                          <div className="flex items-center justify-between">
                            <span className="text-white/60 font-medium truncate">
                              {card.contractName}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleViewContract}
                              className="text-xs px-3 py-1 h-auto"
                            >
                              View Analysis
                            </Button>
                          </div>
                          {card.contractType && (
                            <span className="text-white/40 text-sm">
                              {toProperCase(card.contractType)}
                            </span>
                          )}
                        </div>
                        {/* Card Component */}
                        <div className="transform transition-all hover:scale-[1.01]">
                          <Component data={card.cardData} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

            {/* Empty State */}
            {!isLoadingAny && !hasError && comparisonGroups.length === 0 && (
              <div className="text-center py-16">
                <div className="text-white/50 text-lg mb-4">No cards selected for comparison</div>
                <button
                  onClick={handleClose}
                  className="px-6 py-3 bg-gradient-to-r from-[#547bb5] to-[#6e53b8] rounded-full text-white font-medium hover:opacity-90 transition-opacity"
                >
                  Go Back
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <ContractSelectionPopup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        onSelectContracts={handleContractSelection}
        isDemo={isDemo}
        excludeCurrentContract={false}
        initialSelectedContracts={allContractIds}
      />
    </div>
  )
}
